import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getSegments,
  getStores,
  getCategoriesList,
  getRecentlyBrowseProduct,
  getPreviousOrders,
  getSaveProductCart,
  getStoreBrands,
  reOrder,
} from "./../redux/actions/action-creator";

import ColorInit from "../helper/ColorInit";
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../helper/Preloader";
import Header from "../components/Header";
import MarketPromoBanner from "../components/MarketPromoBanner";
import Services from "../components/Services";
import TopVendors from "../components/TopVendors";
import StoreCategory from "../components/stores/StoreCategory";
import NewArrivalOne from "../components/NewArrivalOne";
import PreviewOrder from "../components/orders/PreviewOrder";
import Brand from "../components/Brand";
import Shipping from "../components/Shipping";
import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";
import BottomFooter from "../components/BottomFooter";
import LocationSelector from "../components/LocationSelector/LocationSelector";
import {
  DEFAULT_DELIVERY_LOCATION,
  LOCATION_STORAGE_KEY,
  hasSelectedDeliveryLocation,
  openDeliveryLocation,
} from "../utils/locationDefaults";
import {
  buildReOrderRequestPayload,
  getReOrderResponseMessage,
  getReOrderSaveCartPayload,
  normalizeReOrderSaveCartPayload,
} from "../utils/reOrder";
import "./HomePage.css";
import { isShoppingSegment } from "../utils/segmentFlow";

const DEFAULT_LOCATION = DEFAULT_DELIVERY_LOCATION;
const HOME_SEGMENT_TITLE = "GROCERY";
const SEGMENT_ID_KEYS = ["segment_id", "segmentId", "id"];
const SLOW_NETWORK_TIMEOUT_MS = 6000;
const NETWORK_TOAST_OPTIONS = {
  position: "top-center",
  autoClose: 4000,
};

const getCellContents = (data) => {
  const candidates = [
    data?.cell_contents,
    data?.response_data?.[0]?.cell_contents,
    data?.response_data,
    data?.data?.cell_contents,
    data?.data?.response_data?.[0]?.cell_contents,
    data?.data?.response_data,
    data?.data,
    data,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const isSuccessResponse = (response) =>
  response?.result === true || response?.result === 1 || response?.result === "1";

const getPreviousOrderItems = (response) => {
  const data = response?.data;
  const possibleLists = [
    data?.cell_contents,
    data?.response_data?.[0]?.cell_contents,
    response?.cell_contents,
  ];

  return possibleLists.find((list) => Array.isArray(list)) || [];
};

const getItemId = (item, keys) => {
  const matchedKey = keys.find(
    (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
  );

  return matchedKey ? item[matchedKey] : null;
};

const hasValidSegmentId = (id) => id !== null && id !== undefined && id !== "" && Number(id) !== 0;

const isHomeSegment = (item) =>
  String(item?.title || "").trim().toUpperCase() === HOME_SEGMENT_TITLE;

const getDynamicSegmentId = (segmentsData) => {
  const directSegmentId = getItemId(segmentsData, SEGMENT_ID_KEYS);

  if (isShoppingSegment(segmentsData) && isHomeSegment(segmentsData) && hasValidSegmentId(directSegmentId)) {
    return directSegmentId;
  }

  const segmentItems = getCellContents(segmentsData);
  const homeSegment = segmentItems.find((item) => {
    const id = getItemId(item, SEGMENT_ID_KEYS);
    return isShoppingSegment(item) && isHomeSegment(item) && hasValidSegmentId(id);
  });

  if (homeSegment) {
    return getItemId(homeSegment, SEGMENT_ID_KEYS);
  }

  const selectedSegment = segmentItems.find((item) => {
    const id = getItemId(item, SEGMENT_ID_KEYS);
    return isShoppingSegment(item) && hasValidSegmentId(id);
  });

  return getItemId(selectedSegment, SEGMENT_ID_KEYS);
};

const getDynamicBusinessSegmentId = (storesData) => {
  const businessSegmentKeys = [
    "business_segment_id",
    "businessSegmentId",
    "business_segment",
    "businessSegment",
  ];
  const directBusinessSegmentId = getItemId(storesData, businessSegmentKeys);

  if (directBusinessSegmentId) {
    return directBusinessSegmentId;
  }

  const storeItems = getCellContents(storesData);
  const selectedStore = storeItems.find((item) =>
    getItemId(item, businessSegmentKeys)
  );

  return getItemId(selectedStore, businessSegmentKeys);
};

const getDynamicSelectedStore = (storesData) => {
  const storeItems = getCellContents(storesData);

  return (
    storeItems.find((item) =>
      getItemId(item, [
        "business_segment_id",
        "businessSegmentId",
        "business_segment",
        "businessSegment",
        "store_id",
        "storeId",
        "id",
        "branch_id",
      ])
    ) ||
    storeItems[0] ||
    null
  );
};

const getStoreBusinessSegmentId = (storeItem) =>
  getItemId(storeItem, [
    "business_segment_id",
    "businessSegmentId",
    "business_segment",
    "businessSegment",
    "store_id",
    "storeId",
    "id",
    "branch_id",
  ]);

const getStoreSegmentId = (storeItem, fallbackSegmentId = "") =>
  getItemId(storeItem, ["segment_id", "segmentId", "segment"]) || fallbackSegmentId;

const appendHomeContextPayload = (formData, userLocation, { includeCountryId = true } = {}) => {
  formData.append("is_dev_stores_enable", 1);
  formData.append("latitude", userLocation.latitude);
  formData.append("longitude", userLocation.longitude);
  formData.append("merchant_id", userLocation.merchant_id);
  if (includeCountryId) {
    formData.append("country_id", userLocation.country_id);
  }
  formData.append("home_screen", 1);
  formData.append("banner_for", 1);

  if (
    userLocation.area !== null &&
    userLocation.area !== undefined &&
    userLocation.area !== ""
  ) {
    formData.append("area", userLocation.area);
  }
};

const buildHomePayload = (userLocation, segmentId = "") => {
  const data = new FormData();

  appendHomeContextPayload(data, userLocation);
  data.append("segment_id", segmentId);

  return data;
};

const hasNetworkError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();

  return (
    (typeof navigator !== "undefined" && navigator.onLine === false) ||
    message.includes("network") ||
    code.includes("network")
  );
};

const hasTimeoutError = (error) =>
  String(error?.message || "").toLowerCase().includes("timeout");

const NoServiceAreaModal = ({ show, onConfirm }) => {
  if (!show) return null;

  return (
    <div className="home-no-service-modal" role="dialog" aria-modal="true">
      <div className="home-no-service-modal__panel">
        <div className="home-no-service-modal__icon">
          <i className="ph ph-map-pin-line" aria-hidden="true" />
        </div>
        <h3>No service available</h3>
        <p>We do not have services available at this location yet.</p>
        <button type="button" onClick={onConfirm}>
          OK
        </button>
      </div>
    </div>
  );
};

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeRequestRef = useRef(0);
  const slowTimerRef = useRef(null);
  const previewReOrderSubmittingRef = useRef("");
  const {
    latitude,
    longitude,
    user_location,
    country_id,
    area,
  } = useSelector((state) => state.auth);
  const locationLatitude = user_location?.latitude;
  const locationLongitude = user_location?.longitude;
  const locationCountryId = user_location?.country_id ?? user_location?.countryId;
  const locationMerchantId = user_location?.merchant_id ?? user_location?.merchantId;
  const locationArea = user_location?.area ?? user_location?.area_id;
  const [servicesLoading, setServicesLoading] = useState(true);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storeCategoriesLoading, setStoreCategoriesLoading] = useState(true);
  const [recentlybrowseLoading, setRecentlybrowseLoading] = useState(true);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [services, setServices] = useState(null);
  const [stores, setStores] = useState(null);
  const [storeCategories, setStoreCategories] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [selectedBusinessSegmentId, setSelectedBusinessSegmentId] = useState("");
  const [recentlybrowse, setRecentlybrowse] = useState(null);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [showPreviewOrders, setShowPreviewOrders] = useState(false);
  const [previewReOrderSubmittingId, setPreviewReOrderSubmittingId] = useState("");
  const [brands, setBrands] = useState(null);
  const [cartContext, setCartContext] = useState({});
  const [noServiceModalOpen, setNoServiceModalOpen] = useState(false);

  const getSavedUserLocation = useCallback(() => {
    try {
      const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      return savedLocation ? JSON.parse(savedLocation) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const savedLocation = getSavedUserLocation();
  const requestLatitude = String(latitude ?? locationLatitude ?? savedLocation?.latitude ?? DEFAULT_LOCATION.latitude);
  const requestLongitude = String(longitude ?? locationLongitude ?? savedLocation?.longitude ?? DEFAULT_LOCATION.longitude);
  const requestCountryId = String(country_id ?? locationCountryId ?? savedLocation?.country_id ?? savedLocation?.countryId ?? DEFAULT_LOCATION.country_id);
  const requestMerchantId = String(locationMerchantId ?? savedLocation?.merchant_id ?? savedLocation?.merchantId ?? DEFAULT_LOCATION.merchant_id);
  const activeArea = area ?? locationArea ?? savedLocation?.area ?? savedLocation?.area_id;
  const requestArea = activeArea === null || activeArea === undefined || activeArea === "" ? undefined : String(activeArea);

  const getActiveUserLocation = useCallback(() => ({
    latitude: requestLatitude,
    longitude: requestLongitude,
    country_id: requestCountryId,
    merchant_id: requestMerchantId,
    area: requestArea,
  }), [requestLatitude, requestLongitude, requestCountryId, requestMerchantId, requestArea]);

  const isCurrentRequest = useCallback(
    (requestId) => activeRequestRef.current === requestId,
    []
  );

  const clearSlowNetworkTimer = useCallback(() => {
    if (slowTimerRef.current) {
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
    }
  }, []);

  const showNetworkWarning = useCallback((message, toastId) => {
    toast.warn(message, {
      ...NETWORK_TOAST_OPTIONS,
      toastId,
    });
  }, []);

  const startSlowNetworkTimer = useCallback(() => {
    clearSlowNetworkTimer();
    slowTimerRef.current = setTimeout(() => {
      showNetworkWarning(
        "Slow network detected. Please wait while we load the latest data.",
        "slow-network-warning"
      );
    }, SLOW_NETWORK_TIMEOUT_MS);
  }, [clearSlowNetworkTimer, showNetworkWarning]);

  const handleApiError = useCallback(
    (error, label) => {
      console.log(`${label} API Error:`, error?.message);

      if (hasTimeoutError(error)) {
        showNetworkWarning(
          "Slow network detected. Please wait while we load the latest data.",
          "slow-network-warning"
        );
      } else if (hasNetworkError(error)) {
        showNetworkWarning(
          "No internet connection. Please check your network.",
          "no-internet-warning"
        );
      }
    },
    [showNetworkWarning]
  );

  const stopDependentLoaders = useCallback(() => {
    setStoresLoading(false);
    setStoreCategoriesLoading(false);
    setRecentlybrowseLoading(false);
    setBrandsLoading(false);
  }, []);

  const resetStoreDependentState = useCallback(() => {
    setStoreCategories(null);
    setRecentlybrowse(null);
    setBrands(null);
    setPreviousOrders([]);
    setShowPreviewOrders(false);
    setSelectedStore(null);
    setSelectedBusinessSegmentId("");
    setCartContext({});
  }, []);

  const loadStoreSections = useCallback(
    async ({ requestId, userLocation, segmentId, businessSegmentId }) => {
      if (!businessSegmentId) {
        if (isCurrentRequest(requestId)) {
          setStoreCategories(null);
          setRecentlybrowse(null);
          setBrands(null);
          setPreviousOrders([]);
          setShowPreviewOrders(false);
          setStoreCategoriesLoading(false);
          setRecentlybrowseLoading(false);
          setBrandsLoading(false);
        }
        return;
      }

      if (isCurrentRequest(requestId)) {
        setStoreCategoriesLoading(true);
        setRecentlybrowseLoading(true);
        setBrandsLoading(true);
      }

      const categoryData = new FormData();
      categoryData.append("business_segment_id", businessSegmentId);
      categoryData.append("segment_id", segmentId);
      categoryData.append("calling_from", "website_screen");
      categoryData.append("is_website_calling", 1);

      const recentProductData = new FormData();
      recentProductData.append("segment_id", segmentId);
      recentProductData.append("latitude", userLocation.latitude);
      recentProductData.append("longitude", userLocation.longitude);
      recentProductData.append("business_segment_id", businessSegmentId);

      const previousOrdersData = new FormData();
      previousOrdersData.append("segment_id", segmentId);
      previousOrdersData.append("merchant_id", userLocation.merchant_id);
      previousOrdersData.append("business_segment_id", businessSegmentId);

      const brandData = new FormData();
      brandData.append("business_segment_id", businessSegmentId);
      brandData.append("segment_id", segmentId);
      brandData.append("merchant_id", userLocation.merchant_id);
      brandData.append("calling_from", "category_screen");
      brandData.append("page", 1);
      brandData.append("per_page", 10);

      await Promise.all([
        dispatch(getCategoriesList(categoryData))
          .then((response) => {
            if (isCurrentRequest(requestId)) {
              setStoreCategories(response?.result ? response.data : null);
            }
          })
          .catch((error) => {
            handleApiError(error, "Home store category");
            if (isCurrentRequest(requestId)) setStoreCategories(null);
          })
          .finally(() => {
            if (isCurrentRequest(requestId)) setStoreCategoriesLoading(false);
          }),
        dispatch(getRecentlyBrowseProduct(recentProductData))
          .then((response) => {
            if (isCurrentRequest(requestId)) {
              setRecentlybrowse(response?.result ? response.data : null);
            }
          })
          .catch((error) => {
            handleApiError(error, "Recently browse");
            if (isCurrentRequest(requestId)) setRecentlybrowse(null);
          })
          .finally(() => {
            if (isCurrentRequest(requestId)) setRecentlybrowseLoading(false);
          }),
        dispatch(getPreviousOrders(previousOrdersData))
          .then((response) => {
            if (!isCurrentRequest(requestId)) return;

            const orderItems = getPreviousOrderItems(response);
            setPreviousOrders(orderItems);
            setShowPreviewOrders(isSuccessResponse(response) && orderItems.length > 1);
          })
          .catch((error) => {
            handleApiError(error, "Previous orders");
            if (isCurrentRequest(requestId)) {
              setPreviousOrders([]);
              setShowPreviewOrders(false);
            }
          }),
        dispatch(getStoreBrands(brandData))
          .then((response) => {
            if (isCurrentRequest(requestId)) {
              setBrands(response?.result ? response.data?.response_data?.[0] || response.data || null : null);
            }
          })
          .catch((error) => {
            handleApiError(error, "Brands");
            if (isCurrentRequest(requestId)) setBrands(null);
          })
          .finally(() => {
            if (isCurrentRequest(requestId)) setBrandsLoading(false);
          }),
      ]);
    },
    [dispatch, handleApiError, isCurrentRequest]
  );

  const loadStoresForSegment = useCallback(
    async ({ requestId, userLocation, segmentId }) => {
      if (isCurrentRequest(requestId)) {
        setStoresLoading(true);
        resetStoreDependentState();
      }

      try {
        const storesResponse = await dispatch(
          getStores(buildHomePayload(userLocation, segmentId))
        );
        if (!isCurrentRequest(requestId)) return;

        const storesData = storesResponse?.result ? storesResponse.data : null;
        const storeItems = getCellContents(storesData);

        setStores(storesData);

        if (!storeItems.length) {
          setCartContext({
            segment_id: segmentId,
            business_segment_id: "",
          });
          stopDependentLoaders();
          return;
        }

        const nextSelectedStore = getDynamicSelectedStore(storesData);
        const businessSegmentId =
          getStoreBusinessSegmentId(nextSelectedStore) ||
          getDynamicBusinessSegmentId(storesData) ||
          "";
        const nextSegmentId = getStoreSegmentId(nextSelectedStore, segmentId);

        setSelectedStore(nextSelectedStore);
        setSelectedBusinessSegmentId(businessSegmentId);
        setCartContext({
          segment_id: nextSegmentId,
          business_segment_id: businessSegmentId,
        });
        setStoresLoading(false);

        await loadStoreSections({
          requestId,
          userLocation,
          segmentId: nextSegmentId,
          businessSegmentId,
        });
      } catch (error) {
        handleApiError(error, "Stores");
        if (isCurrentRequest(requestId)) {
          setStores(null);
          stopDependentLoaders();
        }
      } finally {
        if (isCurrentRequest(requestId)) {
          setStoresLoading(false);
        }
      }
    },
    [
      dispatch,
      handleApiError,
      isCurrentRequest,
      loadStoreSections,
      resetStoreDependentState,
      stopDependentLoaders,
    ]
  );

  const loadHomeData = useCallback(async () => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;
    const userLocation = getActiveUserLocation();

    setServicesLoading(true);
    setStoresLoading(true);
    setStoreCategoriesLoading(true);
    setRecentlybrowseLoading(true);
    setBrandsLoading(true);
    setServices(null);
    setStores(null);
    setRecentlybrowse(null);
    setBrands(null);
    setPreviousOrders([]);
    setShowPreviewOrders(false);
    setSelectedSegmentId("");
    setSelectedBusinessSegmentId("");
    setSelectedStore(null);
    setStoreCategories(null);
    setCartContext({});
    setNoServiceModalOpen(false);

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      showNetworkWarning(
        "No internet connection. Please check your network.",
        "no-internet-warning"
      );
      setServicesLoading(false);
      stopDependentLoaders();
      return;
    }

    startSlowNetworkTimer();

    try {
      let nextSegmentId = "";

      try {
        const segmentsResponse = await dispatch(
          getSegments(buildHomePayload(userLocation, ""))
        );
        if (!isCurrentRequest(requestId)) return;

        if (segmentsResponse?.result) {
          setServices(segmentsResponse.data);
          const serviceItems = getCellContents(segmentsResponse.data);

          if (!serviceItems.length) {
            setNoServiceModalOpen(true);
            setStores(null);
            stopDependentLoaders();
            return;
          }

          nextSegmentId = getDynamicSegmentId(segmentsResponse.data) || "";
          setSelectedSegmentId(nextSegmentId);
        } else {
          setServices(null);
        }
      } catch (error) {
        handleApiError(error, "Segments");
        if (isCurrentRequest(requestId)) {
          setServices(null);
        }
      } finally {
        if (isCurrentRequest(requestId)) setServicesLoading(false);
      }

      if (!isCurrentRequest(requestId)) return;

      if (!nextSegmentId) {
        setStores(null);
        stopDependentLoaders();
        return;
      }

      await loadStoresForSegment({
        requestId,
        userLocation,
        segmentId: nextSegmentId,
      });
    } finally {
      if (isCurrentRequest(requestId)) {
        clearSlowNetworkTimer();
      }
    }
  }, [
    clearSlowNetworkTimer,
    dispatch,
    getActiveUserLocation,
    handleApiError,
    isCurrentRequest,
    loadStoresForSegment,
    showNetworkWarning,
    startSlowNetworkTimer,
    stopDependentLoaders,
  ]);

  const handlePreviewOrderAgain = useCallback(
    async (order, orderActionId) => {
      if (previewReOrderSubmittingRef.current) return;

      const { payload, bookingOrderId, segmentId } = buildReOrderRequestPayload(
        order,
        HOME_SEGMENT_TITLE
      );

      if (!bookingOrderId) {
        toast.warn("Order id not found.", NETWORK_TOAST_OPTIONS);
        return;
      }

      const loadingOrderId = String(orderActionId || bookingOrderId);
      previewReOrderSubmittingRef.current = loadingOrderId;
      setPreviewReOrderSubmittingId(loadingOrderId);

      try {
        let reOrderResponse;

        try {
          reOrderResponse = await dispatch(reOrder(payload));
        } catch (error) {
          toast.warn(
            getReOrderResponseMessage(error, "Unable to re-order this order."),
            NETWORK_TOAST_OPTIONS
          );
          return;
        }

        if (!isSuccessResponse(reOrderResponse)) {
          toast.warn(
            getReOrderResponseMessage(reOrderResponse, "Unable to re-order this order."),
            NETWORK_TOAST_OPTIONS
          );
          return;
        }

        const saveCartPayload = getReOrderSaveCartPayload(reOrderResponse);

        if (!saveCartPayload) {
          toast.warn(
            getReOrderResponseMessage(
              reOrderResponse,
              "Unable to prepare this order for cart."
            ),
            NETWORK_TOAST_OPTIONS
          );
          return;
        }

        try {
          const saveCartResponse = await dispatch(
            getSaveProductCart(
              normalizeReOrderSaveCartPayload({
                payload: saveCartPayload,
                ordersDetail: order,
                location: getActiveUserLocation(),
                segmentId,
              })
            )
          );

          if (isSuccessResponse(saveCartResponse)) {
            navigate("/cart", { replace: true });
            return;
          }

          toast.warn(
            getReOrderResponseMessage(saveCartResponse, "Unable to add reordered items to cart."),
            NETWORK_TOAST_OPTIONS
          );
        } catch (error) {
          toast.warn(
            getReOrderResponseMessage(error, "Unable to add reordered items to cart."),
            NETWORK_TOAST_OPTIONS
          );
        }
      } finally {
        previewReOrderSubmittingRef.current = "";
        setPreviewReOrderSubmittingId("");
      }
    },
    [dispatch, getActiveUserLocation, navigate]
  );

  const handleNoServiceConfirm = useCallback(() => {
    setNoServiceModalOpen(false);
    openDeliveryLocation();
  }, []);

  useEffect(() => {
    loadHomeData();
    return () => {
      activeRequestRef.current += 1;
      clearSlowNetworkTimer();
    };
  }, [clearSlowNetworkTimer, loadHomeData]);

  useEffect(() => {
    const handleOffline = () => {
      showNetworkWarning(
        "No internet connection. Please check your network.",
        "no-internet-warning"
      );
    };
    const connection =
      typeof navigator !== "undefined"
        ? navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection
        : null;
    const handleConnectionChange = () => {
      const effectiveType = String(connection?.effectiveType || "").toLowerCase();

      if (effectiveType === "slow-2g" || effectiveType === "2g") {
        showNetworkWarning(
          "Slow network detected. Please wait while we load the latest data.",
          "slow-network-warning"
        );
      }
    };

    window.addEventListener("offline", handleOffline);
    connection?.addEventListener?.("change", handleConnectionChange);

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      handleOffline();
    }
    handleConnectionChange();

    return () => {
      window.removeEventListener("offline", handleOffline);
      connection?.removeEventListener?.("change", handleConnectionChange);
    };
  }, [showNetworkWarning]);

  return (

    <div className="bg-white home-page-shell">
      {/* ColorInit */}
      <ColorInit color={true} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#FA6400" />

      {/* Preloader */}
      <Preloader />

      {/* Header */}
      <Header category={false} mobileHomeHeader className="home-page-sticky-header" />

      <h1 className="visually-hidden">Grocery Stores Near You | RGHT NOW</h1>

      {/* LocationSelector */}
      <LocationSelector category={false} services={services} />

      {/* Banner */}
      <MarketPromoBanner segmentId={selectedSegmentId} enabled={!servicesLoading && Boolean(selectedSegmentId)} />

      {/* Services */}
      <Services
        services={services}
        loading={servicesLoading}
        selectedServiceId={selectedSegmentId}
      />

      {/* TopVendors */}
      <TopVendors
        stores={stores}
        loading={storesLoading}
        selectedStoreId={selectedBusinessSegmentId}
      />

      {/* StoreCategory */}
      <StoreCategory
        storeDetail={selectedStore || {}}
        categories={storeCategories}
        storeLoading={false}
        categoriesLoading={storeCategoriesLoading}
        businessSegmentId={cartContext.business_segment_id}
        segmentId={cartContext.segment_id}
        storeId={
          selectedStore?.store_id ||
          selectedStore?.storeId ||
          selectedStore?.id ||
          selectedStore?.branch_id ||
          ""
        }
        showStoreHeader={false}
      />

      {/* RecentlyViewed */}
      {/*<RecentlyViewed recentlybrowse={recentlybrowse}/>*/}
      <NewArrivalOne
        recentlybrowse={recentlybrowse}
        loading={recentlybrowseLoading}
        cartContext={cartContext}
      />

      {/* PreviewOrder */}
      {showPreviewOrders && (
        <PreviewOrder
          orders={previousOrders}
          onOrderAgain={handlePreviewOrderAgain}
          orderAgainLoadingId={previewReOrderSubmittingId}
        />
      )}

      {/* Brand */}
      <Brand brands={brands} loading={brandsLoading}/>

      {/* Shipping */}
      <Shipping />

      {/* Newsletter */}
      <Newsletter />

      {/* Footer */}
      <Footer />

      {/* BottomFooter */}
      <BottomFooter mobileAppNav />

      <NoServiceAreaModal
        show={noServiceModalOpen && hasSelectedDeliveryLocation(user_location)}
        onConfirm={handleNoServiceConfirm}
      />

    </div>
  );
};

export default HomePage;
